import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkerAlt, faCalendar, faUser, faArrowLeft, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import api from '@/services/api'
import Spinner from '@/components/Spinner'
import { StatusBadge } from '@/utils/badges'

/**
 * Venue interface
 */
interface VenueDetail {
  id: string
  organizerId: string
  organizer: {
    id: string
    email: string
    name: string
    organization: string | null
    subscriptionType: string
    subscriptionStatus: string
  } | null
  name: string
  address: string | null
  city: string
  country: string | null
  googleMapsUrl: string | null
  eventsCount: number
  events: Array<{
    id: string
    name: string
    date: string | null
    status: string
    createdAt: string
  }>
  createdAt: string
  updatedAt: string
}

/**
 * Venue Details Page
 * Dark mode geek aesthetic - Desktop only
 */
export default function VenueDetailsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const [venue, setVenue] = useState<VenueDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch venue details
  useEffect(() => {
    if (!id) {
      navigate('/venues')
      return
    }

    const fetchVenueDetails = async () => {
      try {
        const response = await api.get(`/superadmin/venues/${id}`)
        if (response.data.success) {
          setVenue(response.data.data)
        }
      } catch (err: any) {
        setError(err.response?.data?.error || t('common.error'))
      } finally {
        setLoading(false)
      }
    }

    fetchVenueDetails()
  }, [id, navigate, t])

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Spinner />
      </div>
    )
  }

  if (error || !venue) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-300 font-mono text-sm">
            <span className="text-red-500">{'[ERROR]'}</span> {error || 'Lieu non trouvé'}
          </p>
        </div>
        <button
          onClick={() => navigate('/venues')}
          className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-bold rounded-lg transition"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Retour à la liste
        </button>
      </div>
    )
  }

  // Define detail sections
  const detailSections = [
    {
      key: 'general',
      label: 'Informations générales',
      rows: [
        {
          key: 'name',
          label: 'Nom',
          value: venue.name,
        },
        {
          key: 'address',
          label: 'Adresse',
          value: venue.address || '—',
        },
        {
          key: 'city',
          label: 'Ville',
          value: venue.city,
        },
        {
          key: 'country',
          label: 'Pays',
          value: venue.country || '—',
        },
        {
          key: 'googleMapsUrl',
          label: 'Actions',
          value: venue.googleMapsUrl ? (
            <button
              onClick={() => window.open(venue.googleMapsUrl!, '_blank', 'noopener,noreferrer')}
              className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg transition text-xs font-mono flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faMapMarkerAlt} className="text-xs" />
              Ouvrir Google Maps
              <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs" />
            </button>
          ) : (
            <span className="text-slate-500">Aucun lien Google Maps disponible</span>
          ),
        },
        {
          key: 'createdAt',
          label: 'Date de création',
          value: new Date(venue.createdAt).toLocaleDateString('fr-FR'),
        },
      ],
    },
    {
      key: 'organizer',
      label: 'Organisateur',
      rows: [
        {
          key: 'organizerName',
          label: 'Nom',
          value: venue.organizer?.name || '—',
        },
        {
          key: 'organizerEmail',
          label: 'Email',
          value: venue.organizer?.email || '—',
        },
        {
          key: 'organizerOrganization',
          label: 'Organisation',
          value: venue.organizer?.organization || '—',
        },
        {
          key: 'organizerSubscription',
          label: 'Plan',
          value: (() => {
            const subType = venue.organizer?.subscriptionType || 'free'
            return subType === 'free' ? 'Gratuit' : subType
          })(),
        },
        {
          key: 'organizerLink',
          label: 'Actions',
          value: venue.organizer ? (
            <button
              onClick={() => navigate(`/organizers/${venue.organizerId}`)}
              className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white font-bold rounded-lg transition text-xs font-mono"
            >
              Voir organisateur
            </button>
          ) : (
            '—'
          ),
        },
      ],
    },
    {
      key: 'stats',
      label: 'Statistiques',
      rows: [
        {
          key: 'eventsCount',
          label: 'Nombre d\'événements',
          value: venue.eventsCount,
        },
      ],
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={() => navigate('/venues')}
            className="mb-4 text-slate-400 hover:text-cyan-400 transition font-mono text-sm flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            Retour à la liste
          </button>
          <h1 className="text-4xl font-black bg-linear-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            {venue.name}
          </h1>
          <p className="text-slate-400 mt-2 font-mono text-sm">
            {'> '} <span className="text-cyan-400">venue</span> {' --id='}
            <span className="text-blue-400">{venue.id}</span>
          </p>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-900/30 border border-red-500/50 rounded-lg p-4">
          <p className="text-red-300 font-mono text-sm">
            <span className="text-red-500">{'[ERROR]'}</span> {error}
          </p>
        </div>
      )}

      {/* Detail Sections */}
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        {detailSections.map((section) => (
          <div key={section.key} className="mb-8 last:mb-0">
            <h2 className="text-lg font-bold text-white font-mono mb-4 border-b border-slate-700/50 pb-2">
              {section.label}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {section.rows.map((row) => (
                <div key={row.key} className="flex flex-col">
                  <span className="text-xs font-mono font-bold text-cyan-400 uppercase tracking-wider mb-1">
                    {row.label}
                  </span>
                  <span className="text-slate-200 font-mono text-sm">{row.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Events List Section */}
      {venue.events && venue.events.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-700/50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white font-mono">
                  {'> '}
                  <span className="text-cyan-400">events</span>
                  {' --venue='}
                  <span className="text-blue-400">{venue.name}</span>
                </h2>
                <p className="text-slate-400 text-xs mt-1 font-mono">
                  Liste des événements utilisant ce lieu ({venue.events.length})
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-800/50 border-b border-slate-700/50">
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left font-mono text-cyan-400 text-xs uppercase tracking-wider">
                    Créé le
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/50">
                {venue.events.map((event) => (
                  <tr
                    key={event.id}
                    onClick={() => navigate(`/events/${event.id}`)}
                    className="hover:bg-slate-800/50 transition group cursor-pointer"
                  >
                    <td className="px-6 py-4 text-slate-200 font-semibold">{event.name}</td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {event.date
                        ? new Date(event.date).toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '—'}
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={event.status} />
                    </td>
                    <td className="px-6 py-4 text-slate-300 font-mono text-xs">
                      {new Date(event.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
